<template>
  <div class="chapter-section-status-circle">
    <StatusCircle :fill="statusCircleFill" :title="titleText" />
  </div>
</template>

<script>
import StatusCircle, { StatusCircleFill } from '@/components/ui/StatusCircle'
import { ChapterSectionType } from '@/enums'
import { BookStoreNamespacedTypes } from '@/store/books'
import { LessonStoreNamespacedTypes } from '@/store/lessons'
import { isUserSelectedUnderstoodValue } from '@/utils/lessons'

export default {
  name: 'ChapterSectionStatusCircle',
  components: { StatusCircle },
  props: {
    chapterId: {
      type: Number,
      required: true,
    },
    sectionType: {
      type: Number,
      required: true,
      validator: sectionType =>
        Object.values(ChapterSectionType).includes(sectionType),
    },
    sectionTypeId: {
      type: Number,
      required: false,
    },
  },
  computed: {
    /**
     * @return {boolean}
     */
    hasSelfTraining() {
      if (this.sectionType === ChapterSectionType.LESSON) {
        return this.$store.getters[
          LessonStoreNamespacedTypes.getters.HAS_5_SHARP_TEST
        ](this.sectionTypeId)
      }

      return false
    },

    /**
     * @return {boolean}
     */
    isLessonCompleted() {
      if (this.sectionType === ChapterSectionType.LESSON) {
        return isUserSelectedUnderstoodValue(
          this.$store.getters[
            LessonStoreNamespacedTypes.getters.GET_UNDERSTOOD_STATUS
          ](this.sectionTypeId)
        )
      }

      return false
    },

    /**
     * @return {boolean}
     */
    isSelfTrainingCompleted() {
      if (this.sectionType === ChapterSectionType.LESSON) {
        return this.$store.getters[
          BookStoreNamespacedTypes.getters.IS_SELF_TRAINING_COMPLETED
        ](this.chapterId, this.sectionTypeId)
      }

      return false
    },

    /**
     * @return {boolean}
     */
    isTrialExamCompleted() {
      if (this.sectionType === ChapterSectionType.TRIAL_EXAM) {
        return this.$store.getters[
          BookStoreNamespacedTypes.getters.IS_TRIAL_EXAM_COMPLETED
        ](this.chapterId, this.sectionTypeId)
      }

      return false
    },

    /**
     * @return {StatusCircleFill}
     */
    statusCircleFill() {
      switch (this.sectionType) {
        case ChapterSectionType.INTRO:
          return StatusCircleFill.NONE

        case ChapterSectionType.LESSON:
          {
            if (
              this.isLessonCompleted &&
              (!this.hasSelfTraining || this.isSelfTrainingCompleted)
            ) {
              return StatusCircleFill.FULL
            }

            if (this.isLessonCompleted) {
              return StatusCircleFill.LEFT
            }

            if (this.isSelfTrainingCompleted) {
              return StatusCircleFill.RIGHT
            }
          }

          break

        case ChapterSectionType.TRIAL_EXAM:
          if (this.isTrialExamCompleted) {
            return StatusCircleFill.FULL
          }
      }

      return StatusCircleFill.NONE
    },

    /**
     * @return {string}
     */
    titleText() {
      let titleTextParts = []

      switch (this.sectionType) {
        case ChapterSectionType.INTRO:
          titleTextParts.push(
            this.$t('components.chapter-section-status-circle.introduction')
          )

          break
        case ChapterSectionType.LESSON:
          if (this.isLessonCompleted) {
            titleTextParts.push(
              this.$t(
                'components.chapter-section-status-circle.lesson-completed'
              )
            )
          } else {
            titleTextParts.push(
              this.$t(
                'components.chapter-section-status-circle.lesson-not-completed'
              )
            )
          }

          if (this.hasSelfTraining) {
            if (this.isSelfTrainingCompleted) {
              titleTextParts.push(
                this.$t(
                  'components.chapter-section-status-circle.self-training-completed'
                )
              )
            } else {
              titleTextParts.push(
                this.$t(
                  'components.chapter-section-status-circle.self-training-not-completed'
                )
              )
            }
          }

          break
        case ChapterSectionType.TRIAL_EXAM:
          if (this.isTrialExamCompleted) {
            titleTextParts.push(
              this.$t(
                'components.chapter-section-status-circle.trial-exam-completed'
              )
            )
          } else {
            titleTextParts.push(
              this.$t(
                'components.chapter-section-status-circle.exam-not-completed'
              )
            )
          }

          break
      }

      return titleTextParts.join('\n')
    },
  },
}
</script>

<style lang="scss" scoped>
.chapter-section-status-circle {
  width: 2em;
  height: 2em;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
